import axios from 'axios';

/**
 * Dify聊天历史管理API服务
 */
class DifyChatAPI {
  constructor(baseURL, apiKey) {
    this.baseURL = baseURL;
    this.apiKey = apiKey;
  }

  /**
   * 获取会话列表
   * @param {string} user - 用户ID
   * @param {string} lastId - 最后一个会话ID，用于分页
   * @param {number} limit - 每页数量
   * @returns {Promise<Object>} 会话列表数据
   */
  async getConversations(user, lastId = '', limit = 10) {
    try {
      // 移除调试日志
      // console.log('Dify API请求配置:', {
      //   url: `${this.baseURL}/v1/conversations`,
      //   apiKey: this.apiKey ? `${this.apiKey.substring(0, 10)}...` : '未配置',
      //   user,
      //   lastId,
      //   limit
      // });
      
      // 尝试不同的认证方式
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      
      // 方式1: Bearer Token
      if (this.apiKey) {
        headers['Authorization'] = `Bearer ${this.apiKey}`;
      }
      
      // 方式2: 如果Bearer失败，尝试其他方式
      // console.log('尝试认证方式:', headers);
      
      const response = await axios.get(`${this.baseURL}/v1/conversations`, {
        params: {
          user,
          last_id: lastId,
          limit
        },
        headers,
        withCredentials: false,
        timeout: 30000
      });
      return response.data;
    } catch (error) {
      console.error('获取会话列表失败:', error);
      throw error;
    }
  }

  /**
   * 获取单个会话的历史消息
   * @param {string} user - 用户ID
   * @param {string} conversationId - 会话ID
   * @param {string} firstId - 当前页第一条聊天记录的ID，用于分页
   * @param {number} limit - 每页数量
   * @returns {Promise<Object>} 消息列表数据
   */
  async getMessages(user, conversationId, firstId = '', limit = 20) {
    try {
      // 移除调试日志
      // console.log('Dify API消息请求配置:', {
      //   url: `${this.baseURL}/v1/messages`,
      //   apiKey: this.apiKey ? `${this.apiKey.substring(0, 10)}...` : '未配置',
      //   user,
      //   conversationId,
      //   firstId,
      //   limit
      // });
      
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      
      if (this.apiKey) {
        headers['Authorization'] = `Bearer ${this.apiKey}`;
      }
      
      // console.log('尝试参数格式1:', { user, conversation_id: conversationId, limit });
      
      const response = await axios.get(`${this.baseURL}/v1/messages`, {
        params: {
          user,
          conversation_id: conversationId,
          limit
        },
        headers,
        withCredentials: false,
        timeout: 30000
      });
      
      return response.data;
    } catch (error) {
      console.error('获取消息列表失败:', error);
      throw error;
    }
  }

  /**
   * 删除会话
   * @param {string} conversationId - 会话ID
   * @param {string} user - 用户标识
   * @returns {Promise<Object>} 删除结果
   */
  async deleteConversation(conversationId, user) {
    try {
      console.log('删除Dify会话:', { conversationId, user });
      
      const response = await axios.delete(`${this.baseURL}/v1/conversations/${conversationId}`, {
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        data: {
          user: user
        },
        withCredentials: false, // Dify API不需要携带credentials
        timeout: 30000 // 30秒超时
      });
      
      console.log('删除会话成功:', response.data);
      return response.data;
    } catch (error) {
      console.error('删除会话失败:', error);
      throw error;
    }
  }

  /**
   * 重命名会话
   * @param {string} conversationId - 会话ID
   * @param {string} name - 新名称
   * @returns {Promise<Object>} 重命名结果
   */
  async renameConversation(conversationId, name) {
    try {
      const response = await axios.put(`${this.baseURL}/v1/conversations/${conversationId}`, {
        name
      }, {
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        withCredentials: false, // Dify API不需要携带credentials
        timeout: 30000 // 30秒超时
      });
      return response.data;
    } catch (error) {
      console.error('重命名会话失败:', error);
      throw error;
    }
  }
}

/**
 * 数据转换工具类
 */
export class DifyDataTransformer {
  /**
   * 将Dify会话数据转换为前端统一格式
   * @param {Object} difyConversation - Dify会话数据
   * @returns {Object} 前端统一格式的会话数据
   */
  static transformConversation(difyConversation) {
    return {
      id: difyConversation.id,
      title: difyConversation.name || '新对话',
      conversation_id: difyConversation.id,
      messages: [], // 消息列表需要单独获取
      updatedAt: difyConversation.updated_at * 1000, // 转换为毫秒
      createdAt: difyConversation.created_at * 1000,
      inputs: difyConversation.inputs || {},
      status: difyConversation.status || 'normal'
    };
  }

  /**
   * 将Dify消息数据转换为前端统一格式
   * @param {Object} difyMessage - Dify消息数据
   * @returns {Object} 前端统一格式的消息数据
   */
  static transformMessage(difyMessage) {
    return {
      id: difyMessage.id,
      role: 'user', // Dify API返回的是用户查询
      content: difyMessage.query || '',
      images: difyMessage.message_files || [],
      created_at: new Date(difyMessage.created_at * 1000).toISOString(), // 转换为ISO字符串格式
      conversation_id: difyMessage.conversation_id || '',
      inputs: difyMessage.inputs || {},
      // AI回复作为下一条消息
      aiReply: {
        role: 'assistant',
        content: difyMessage.answer || '',
        created_at: new Date(difyMessage.created_at * 1000).toISOString(), // 转换为ISO字符串格式
        conversation_id: difyMessage.conversation_id || '',
        retrieverResources: difyMessage.retriever_resources || [],
        feedback: difyMessage.feedback
      }
    };
  }

  /**
   * 将消息列表转换为前端统一格式
   * @param {Array} difyMessages - Dify消息列表
   * @returns {Array} 前端统一格式的消息列表
   */
  static transformMessages(difyMessages) {
    const messages = [];
    
    difyMessages.forEach(difyMessage => {
      // 添加用户消息
      messages.push({
        id: difyMessage.id,
        role: 'user',
        content: difyMessage.query || '',
        images: difyMessage.message_files || [],
        created_at: new Date(difyMessage.created_at * 1000).toISOString(), // 转换为ISO字符串格式
        conversation_id: difyMessage.conversation_id || '',
        inputs: difyMessage.inputs || {}
      });

      // 添加AI回复
      if (difyMessage.answer) {
        messages.push({
          id: `${difyMessage.id}_reply`,
          role: 'assistant',
          content: difyMessage.answer,
          created_at: new Date(difyMessage.created_at * 1000).toISOString(), // 转换为ISO字符串格式
          conversation_id: difyMessage.conversation_id || '',
          retrieverResources: difyMessage.retriever_resources || [],
          feedback: difyMessage.feedback
        });
      }
    });

    return messages;
  }
}

/**
 * 创建Dify聊天API实例
 * @param {string} baseURL - API基础URL
 * @param {string} apiKey - API密钥
 * @returns {DifyChatAPI} API实例
 */
export function createDifyChatAPI(baseURL, apiKey) {
  return new DifyChatAPI(baseURL, apiKey);
}

export default DifyChatAPI; 